"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Check,
  X,
  Plus,
  User,
  Building2,
  Sparkles,
  Pencil,
  Loader2,
  Star,
  Globe,
  MapPin,
  Crop,
  IdCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { ImageCropModal } from "@/components/ui/ImageCropModal";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the property templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked photo already becomes a File from a file input.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// =========================================================================
// TYPES
// =========================================================================
interface ServiceData {
  id?: string | number;
  title?: string;
  headline?: string;
  description?: string;
  service_type?: string;
  serviceType?: string;

  heroImage?: string;
  hero_image?: string;
  hero_image_url?: string;
  image?: string;

  secondaryImage?: string;
  photos?: string[];
  photos_urls?: string[];

  features?: (string | { name?: string; feature_name?: string; id?: number })[];

  phone?: string;
  email?: string;
  website?: string;
  address?: string;

  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agentAvatar?: string;

  companyName?: string;
  company?: string;
  profileImage?: string;
  companyImage?: string;
  companyLogo?: string;
  subject?: string;

  [key: string]: any;
}

interface UserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
  license_number?: string;
}

interface ServiceTemplate4Props {
  serviceData?: ServiceData;
  data?: ServiceData;
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Record<string, any>) => void;
  readOnly?: boolean;
  isPreview?: boolean;
}

// =========================================================================
// CONSTANTS
// =========================================================================
const MAX_FEATURES = 10;

const DEFAULT_FEATURES = [
  { id: 1, feature_name: "Personalized Financial Planning" },
  { id: 2, feature_name: "Competitive Loan Options" },
  { id: 3, feature_name: "Fast & Transparent Process" },
  { id: 4, feature_name: "Expert Guidance Throughout" },
  { id: 5, feature_name: "Flexible Terms & Rates" },
];

// =========================================================================
// HELPER FUNCTIONS
// =========================================================================
const getHeroImageUrl = (data: ServiceData | undefined): string => {
  if (!data) return "";
  return data.heroImage || data.hero_image_url || data.hero_image || data.image || "";
};

const getSecondaryImageUrl = (data: ServiceData | undefined): string => {
  if (!data) return "";
  if (data.secondaryImage && data.secondaryImage.length > 0) return data.secondaryImage;
  if (data.photos_urls && Array.isArray(data.photos_urls) && data.photos_urls.length > 0)
    return data.photos_urls[0];
  if (data.photos && Array.isArray(data.photos) && data.photos.length > 0) {
    const photoUrl = data.photos[0];
    if (photoUrl.startsWith("http") || photoUrl.startsWith("data:")) return photoUrl;
    const baseUrl = axiosInstance.defaults.baseURL || "http://localhost:8000";
    return `${baseUrl}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
  }
  return "";
};

const normalizeFeatures = (
  features: any
): { id?: number; feature_name: string }[] => {
  if (!features || !Array.isArray(features)) return [];
  return features
    .map((f, index) => {
      if (typeof f === "string") return { id: index, feature_name: f.trim() };
      if (typeof f === "object" && f !== null)
        return { id: f.id ?? index, feature_name: (f.name || f.feature_name || "").trim() };
      return { id: index, feature_name: "" };
    })
    .filter((f) => f.feature_name.length > 0);
};

type FeatureRow = {
  id: string;
  feature_name: string;
};

const createFeatureId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const areFeatureRowsEqual = (left: FeatureRow[], right: { feature_name: string }[]) =>
  left.length === right.length &&
  left.every((item, index) => item.feature_name.trim() === right[index]?.feature_name.trim());

const mapFeatureRows = (features: any, previous: FeatureRow[] = []): FeatureRow[] => {
  const normalized = normalizeFeatures(features).map((feature) => ({
    feature_name: feature.feature_name,
  }));

  if (normalized.length === 0) return previous;
  if (areFeatureRowsEqual(previous, normalized)) return previous;

  const mapped = normalized.map((feature, index) => ({
    id: previous[index]?.id ?? createFeatureId(),
    feature_name: feature.feature_name,
  }));

  const trailingEmptyRows = previous
    .slice(normalized.length)
    .filter((row) => !row.feature_name.trim());

  return [...mapped, ...trailingEmptyRows];
};

const mapDefaultFeatureRows = (): FeatureRow[] =>
  DEFAULT_FEATURES.map((feature) => ({
    id: `default-${feature.id}`,
    feature_name: feature.feature_name,
  }));

// =========================================================================
// EDITABLE TEXT COMPONENT
// =========================================================================
const EditableText = ({
  value,
  defaultValue,
  onChange,
  readOnly,
  className = "",
  as = "span",
  multiline = false,
  placeholder = "Click to edit...",
  inputClassName = "",
}: {
  value?: string;
  defaultValue: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  className?: string;
  as?: "span" | "h1" | "h2" | "h3" | "p";
  multiline?: boolean;
  placeholder?: string;
  inputClassName?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => setTempValue(value || ""), [value]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const displayValue = value?.trim() || defaultValue;

  const handleSave = () => { onChange(tempValue.trim()); setIsEditing(false); };
  const handleCancel = () => { setTempValue(value || ""); setIsEditing(false); };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) { e.preventDefault(); handleSave(); }
    if (e.key === "Escape") handleCancel();
  };

  if (readOnly) {
    const Tag = as as keyof JSX.IntrinsicElements;
    return <Tag className={className}>{displayValue || placeholder}</Tag>;
  }

  if (isEditing) {
    return (
      <div className="flex items-start gap-1.5 w-full min-w-0">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`flex-1 min-w-0 bg-slate-800 border-2 border-amber-500 rounded-lg px-2 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-y min-h-[80px] shadow-sm text-sm md:text-base ${inputClassName}`}
            placeholder={placeholder}
            rows={4}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`flex-1 min-w-0 bg-slate-800 border-2 border-amber-500 rounded-lg px-2 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 shadow-sm text-sm md:text-base ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button onClick={handleSave} className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0">
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleCancel} className="p-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex-shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  const Tag = as as keyof JSX.IntrinsicElements;
  return (
    <div className="group relative block w-full min-w-0 max-w-full" onClick={() => setIsEditing(true)} title="Click to edit">
      <Tag className={`${className} border-b-2 border-dashed border-transparent group-hover:border-amber-400/60 transition-all duration-200 pb-0.5 break-words`}>
        {displayValue || placeholder}
      </Tag>
      <Pencil className="absolute -right-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-amber-400" />
    </div>
  );
};

// =========================================================================
// MAIN COMPONENT
// =========================================================================
export default function ServiceTemplate4({
  serviceData: propServiceData,
  data: propData,
  onDirtyChange,
  onChange,
  readOnly = true,
  isPreview = false,
}: ServiceTemplate4Props) {
  const { toast } = useToast();
  const incomingData = propServiceData || propData || {};

  const [localEdits, setLocalEdits] = useState<Partial<ServiceData>>({});
  const [userData, setUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [features, setFeatures] = useState<FeatureRow[]>([]);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>("380x380");
  const [boxSecondaryRes, setBoxSecondaryRes] = useState<string | null>("380x380");
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const secondaryContainerRef = useRef<HTMLDivElement | null>(null);

  const incomingSyncKey = useMemo(
    () =>
      [
        incomingData.id,
        incomingData.title,
        incomingData.description,
        getHeroImageUrl(incomingData),
        getSecondaryImageUrl(incomingData),
        JSON.stringify(incomingData.features ?? []),
      ].join("::"),
    [incomingData]
  );

  const serviceData = useMemo(() => {
    const base = propServiceData || propData || {};
    if (readOnly) return base;
    return { ...base, ...localEdits };
  }, [readOnly, incomingSyncKey, localEdits, propServiceData, propData]);

  const heroImageUrl = useMemo(() => getHeroImageUrl(serviceData), [serviceData]);
  const secondaryImageUrl = useMemo(() => getSecondaryImageUrl(serviceData), [serviceData]);

  useEffect(() => {
    const measure = () => {
      if (heroContainerRef.current) {
        setBoxHeroRes(
          `${Math.round(heroContainerRef.current.clientWidth)}x${Math.round(
            heroContainerRef.current.clientHeight
          )}`
        );
      }
      if (secondaryContainerRef.current) {
        setBoxSecondaryRes(
          `${Math.round(secondaryContainerRef.current.clientWidth)}x${Math.round(
            secondaryContainerRef.current.clientHeight
          )}`
        );
      }
    };

    measure();
    const rafId = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      if (heroContainerRef.current) resizeObserver.observe(heroContainerRef.current);
      if (secondaryContainerRef.current) resizeObserver.observe(secondaryContainerRef.current);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    setFeatures((prev) => {
      const mapped = mapFeatureRows(serviceData.features, prev);
      if (mapped.length > 0) return mapped;
      return prev.length === 0 ? mapDefaultFeatureRows() : prev;
    });
  }, [serviceData.features]);

  const prevIdRef = useRef<string | number | undefined>();
  useEffect(() => {
    if (prevIdRef.current !== incomingData.id) {
      setLocalEdits({});
      setFeatures((prev) => {
        const mapped = mapFeatureRows(incomingData.features, prev);
        return mapped.length > 0 ? mapped : mapDefaultFeatureRows();
      });
      prevIdRef.current = incomingData.id;
    }
  }, [incomingData.id, incomingData.features]);

  const prevReadOnlyRef = useRef(readOnly);
  useEffect(() => {
    if (readOnly && !prevReadOnlyRef.current) {
      setLocalEdits({});
    }
    prevReadOnlyRef.current = readOnly;
  }, [readOnly]);

  useEffect(() => {
    if (readOnly) {
      setFeatures((prev) => {
        const mapped = mapFeatureRows(incomingData.features, prev);
        return mapped.length > 0 ? mapped : prev;
      });
    }
  }, [readOnly, incomingSyncKey, incomingData.features]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(endpoints.me);
        const user = res.data;
        if (!isMounted) return;
        setUserData(user);
        setLocalEdits((prev) => ({
          ...prev,
          profileImage: prev.profileImage || prev.agentAvatar || user.avatar || "",
          companyImage: prev.companyImage || prev.companyLogo || user.company_logo || "",
          agentName: prev.agentName || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
          agentPhone: prev.agentPhone || user.phone_number || "",
          agentEmail: prev.agentEmail || user.email || "",
          company: prev.company || prev.companyName || user.company || "",
        }));
      } catch (err) {
        console.error("Failed to load user data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUserData();
    return () => { isMounted = false; };
  }, []);

  const handleChange = useCallback((field: keyof ServiceData, value: any) => {
    setLocalEdits((prev) => {
      const next = { ...prev, [field]: value };
      onDirtyChange?.(true);
      onChange?.({ [field]: value });
      return next;
    });
  }, [onDirtyChange, onChange]);

  const saveFieldToBackend = useCallback(async (field: string, value: any) => {
    if (!serviceData.id) return;
    setSavingField(field);
    try {
      const formData = new FormData();
      if (field === "features" && Array.isArray(value)) {
        const featureNames = value
          .map((f: any) => String(f?.feature_name ?? f ?? "").trim())
          .filter((f: string) => f.length > 0);
        formData.append("features", JSON.stringify(featureNames));
      } else {
        formData.append(field, String(value));
      }
      await axiosInstance.patch(`${endpoints.service_listings}${serviceData.id}/`, formData);
      const { data } = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
      const heroRaw = data.hero_image_url || data.hero_image || "";
      const baseUrl = axiosInstance.defaults.baseURL || "";
      const toFull = (url: string) =>
        !url ? "" : url.startsWith("http") ? url : `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
      const heroFull = toFull(heroRaw);
      const photos = (Array.isArray(data.photos) ? data.photos : []).map((p: string) => toFull(p));
      const synced: Record<string, any> = {
        title: data.title,
        description: data.description,
        service_type: data.service_type,
        serviceType: data.service_type,
        phone: data.phone,
        email: data.email,
        website: data.website,
        address: data.address,
        heroImage: heroFull,
        hero_image_url: heroFull,
        image: heroFull,
        photos,
        photos_urls: photos,
        secondaryImage: photos[0] || "",
        features: data.features,
      };
      setLocalEdits((prev) => ({ ...prev, ...synced, [field]: value }));
      onChange?.(synced);
    } catch (error) {
      console.error(`[ServiceTemplate4] Failed to save ${field}:`, error);
    } finally {
      setSavingField(null);
    }
  }, [serviceData.id, onChange]);

  const handleHeroImageUpload = async (file: File, options?: { captureOriginal?: boolean }) => {
    // captureOriginal defaults to true: a genuinely new photo pick becomes
    // its own untouched "original" so Edit Image can always re-crop from
    // the full source later. The Edit Image crop-confirm path reuses this
    // same handler and passes captureOriginal: false, so re-cropping an
    // already-cropped photo doesn't overwrite the true original with the crop.
    const captureOriginal = options?.captureOriginal ?? true;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (boxHeroRes) {
        const img = new Image();
        img.onload = () => {
          const parts = boxHeroRes.split(/×|x/).map((s) => Number(s.trim()));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            const mismatch = img.naturalWidth !== rw || img.naturalHeight !== rh;
            if (mismatch) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${img.naturalWidth}x${img.naturalHeight}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
              });
            }
          }        // ✅ closing } for if(parts.length===2)
        };
        img.src = dataUrl;
      }
      setLocalEdits((prev) => ({ ...prev, heroImage: dataUrl, hero_image_url: dataUrl }));
      onDirtyChange?.(true);
      onChange?.({ heroImage: dataUrl });
      if (captureOriginal) setOriginalHeroImage(dataUrl);
    };
    reader.readAsDataURL(file);
    if (serviceData.id) {
      setSavingField("heroImage");
      try {
        const formData = new FormData();
        formData.append("hero_image", file);
        const response = await axiosInstance.patch(`${endpoints.service_listings}${serviceData.id}/`, formData);
        if (response.data?.hero_image_url) {
          setLocalEdits((prev) => ({ ...prev, heroImage: response.data.hero_image_url, hero_image_url: response.data.hero_image_url }));
          if (captureOriginal) {
            setOriginalHeroImage(response.data.hero_image_url);
            axiosInstance
              .patch(`${endpoints.service_listings}${serviceData.id}/`, {
                original_hero_image: response.data.hero_image_url,
              })
              .catch((err) => {
                console.error("Failed to seed original hero image:", err);
              });
          }
        }
      } catch (error) {
        console.error("Failed to upload hero image:", error);
      } finally {
        setSavingField(null);
      }
    }
  };

  const handleSecondaryImageUpload = async (file: File, options?: { captureOriginal?: boolean }) => {
    // See handleHeroImageUpload above for why captureOriginal defaults to
    // true but is passed as false from the Edit Image crop-confirm path.
    const captureOriginal = options?.captureOriginal ?? true;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (boxSecondaryRes) {
        const img = new Image();
        img.onload = () => {
          const parts = boxSecondaryRes.split(/×|x/).map((s) => Number(s.trim()));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            toast({
              title: "Resolution Suggestion",
              description: `Uploaded image is ${img.naturalWidth}x${img.naturalHeight}. Recommended: ${boxSecondaryRes}. If possible, upload this resolution for best results.`,
            });
          }
        };
        img.src = dataUrl;
      }
      const newPhotos = [dataUrl, ...(serviceData.photos || []).slice(1)];
      const newPhotosUrls = [dataUrl, ...(serviceData.photos_urls || []).slice(1)];
      setLocalEdits((prev) => ({ ...prev, secondaryImage: dataUrl, photos_urls: newPhotosUrls, photos: newPhotos }));
      onDirtyChange?.(true);
      onChange?.({ secondaryImage: dataUrl, photos: newPhotos, photos_urls: newPhotosUrls });
      if (captureOriginal) setOriginalSecondaryImage(dataUrl);
    };
    reader.readAsDataURL(file);
    if (serviceData.id) {
      setSavingField("secondaryImage");
      try {
        const formData = new FormData();
        formData.append("photo_1", file);
        const response = await axiosInstance.patch(`${endpoints.service_listings}${serviceData.id}/`, formData, {
          headers: { "Content-Type": undefined as any },
          transformRequest: [(data) => data],
        });
        const serverPhotosUrls = response.data?.photos_urls || [];
        const serverPhotos = response.data?.photos || serverPhotosUrls;
        const serverUrl = serverPhotosUrls[0] || serverPhotos[0] || null;
        if (serverUrl) {
          setLocalEdits((prev) => ({ ...prev, secondaryImage: serverUrl, photos_urls: serverPhotosUrls, photos: serverPhotos }));
          onChange?.({ secondaryImage: serverUrl, photos: serverPhotos, photos_urls: serverPhotosUrls });
          if (captureOriginal) {
            setOriginalSecondaryImage(serverUrl);
            axiosInstance
              .patch(`${endpoints.service_listings}${serviceData.id}/`, {
                original_photos: [serverUrl],
              })
              .catch((err) => {
                console.error("Failed to seed original secondary image:", err);
              });
          }
        }
      } catch (error) {
        console.error("Failed to upload secondary image:", error);
      } finally {
        setSavingField(null);
      }
    }
  };

  // =========================================================================
  // EDIT IMAGE (crop/zoom the hero + secondary photo, non-destructively)
  // =========================================================================
  const EDIT_CROP_W = 380;
  const EDIT_CROP_H = 380; // matches the square hero/secondary box

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalHeroImage, setOriginalHeroImage] = useState<string>("");

  const openEditImage = async () => {
    if (readOnly || !heroImageUrl) return;

    let originalSource = originalHeroImage || (serviceData as any)?.original_hero_image || "";

    if (!originalSource && serviceData.id) {
      try {
        const res = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
        originalSource = res?.data?.original_hero_image || "";
      } catch (err) {
        console.error("Failed to check for existing original hero image:", err);
      }
    }

    if (!originalSource) {
      originalSource = heroImageUrl;
      if (serviceData.id && !heroImageUrl.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceData.id}/`, {
            original_hero_image: heroImageUrl,
          })
          .catch((err) => {
            console.error("Failed to seed original hero image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    // Reuse the existing, unmodified hero upload handler as-is, so the
    // crop-confirm path saves exactly like a normal re-upload.
    const file = dataUrlToFile(croppedDataUrl, `edited-hero-${Date.now()}.png`);
    handleHeroImageUpload(file, { captureOriginal: false });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  const [secondaryCropModal, setSecondaryCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalSecondaryImage, setOriginalSecondaryImage] = useState<string>("");

  const openEditSecondaryImage = async () => {
    if (readOnly || !secondaryImageUrl) return;

    let originalSource =
      originalSecondaryImage || ((serviceData as any)?.original_photos?.[0] ?? "");

    if (!originalSource && serviceData.id) {
      try {
        const res = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
        originalSource = res?.data?.original_photos?.[0] || "";
      } catch (err) {
        console.error("Failed to check for existing original secondary image:", err);
      }
    }

    if (!originalSource) {
      originalSource = secondaryImageUrl;
      if (serviceData.id && !secondaryImageUrl.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceData.id}/`, {
            original_photos: [secondaryImageUrl],
          })
          .catch((err) => {
            console.error("Failed to seed original secondary image:", err);
          });
      }
    }

    setOriginalSecondaryImage(originalSource);
    setSecondaryCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleSecondaryEditImageConfirm = (croppedDataUrl: string) => {
    const file = dataUrlToFile(croppedDataUrl, `edited-secondary-${Date.now()}.png`);
    handleSecondaryImageUpload(file, { captureOriginal: false });
    setSecondaryCropModal({ open: false, imageUrl: "" });
  };

  const handleSecondaryEditImageCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "" });
  };

  const addFeature = useCallback(() => {
    if (readOnly || features.length >= MAX_FEATURES) return;
    setFeatures((prev) => [...prev, { id: createFeatureId(), feature_name: "" }]);
    onDirtyChange?.(true);
  }, [readOnly, features.length, onDirtyChange]);

  const handleFeatureChange = useCallback((index: number, value: string) => {
    setFeatures((prev) => { const u = [...prev]; u[index] = { ...u[index], feature_name: value }; return u; });
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const handleFeatureBlur = useCallback((index: number) => {
    const featureNames = features
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    handleChange("features", featureNames);
    void saveFieldToBackend("features", features);
  }, [features, handleChange, saveFieldToBackend]);

  const handleFeatureDelete = useCallback((index: number) => {
    if (features.length <= 1) return;
    const updated = features.filter((_, i) => i !== index);
    const featureNames = updated
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    setFeatures(updated);
    handleChange("features", featureNames);
    void saveFieldToBackend("features", updated);
  }, [features.length, handleChange, saveFieldToBackend]);

  // ── Computed display values ──────────────────────────────────────────────
  const title = serviceData.title?.trim() || "Financial Services";
  const description = serviceData.description?.trim() || "We provide expert financial guidance and tailored solutions to help you achieve your goals with confidence.";

  const agentFullName =
    serviceData.agentName ||
    incomingData.agentName ||
    (userData?.first_name ? `${userData.first_name} ${userData.last_name || ""}`.trim() : "") ||
    "Your Name";

  const displayPhone = userData?.phone_number || serviceData.phone || serviceData.agentPhone || incomingData.phone || incomingData.agentPhone || "";
  const displayEmail = userData?.email || serviceData.email || serviceData.agentEmail || incomingData.email || incomingData.agentEmail || "";
  const displayWebsite = serviceData.website || incomingData.website || "";
  const displayAddress = serviceData.address || incomingData.address || "";
  const displayCompanyName = userData?.company || serviceData.companyName || serviceData.company || incomingData.companyName || incomingData.company || "Your Company";
  const displayAvatar = serviceData.profileImage || serviceData.agentAvatar || incomingData.profileImage || incomingData.agentAvatar || userData?.avatar || "";
  const displayCompanyLogo = serviceData.companyImage || serviceData.companyLogo || incomingData.companyImage || incomingData.companyLogo || userData?.company_logo || "";

  const heroInputRef = useRef<HTMLInputElement>(null);
  const secondaryInputRef = useRef<HTMLInputElement>(null);

  if (loading && !isPreview) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
          <p className="text-slate-300">Loading template...</p>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: "touch", transform: "rotateX(180deg)" }}>
      <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl max-w-none mx-auto text-white relative" style={{ minWidth: "900px", transform: "rotateX(180deg)" }}>

        {/*
        ══════════════════════════════════════════════════════════════
        RESPONSIVE CONTACT CARD STYLES
        ══════════════════════════════════════════════════════════════
        Strategy (mobile-first):
        • < 520px  : single column — avatars centred row on top,
                     name + contact details centred below.
        • ≥ 520px  : side-by-side row — avatars left (fixed size),
                     details right (flex:1, min-width:0).

        Key rules:
        • .st4-contact-inner   — flex-col → flex-row at 520px
        • .st4-avatars         — always a horizontal pair, fixed sizes
        • .st4-details         — flex:1; min-width:0; overflow:hidden
        • .st4-contact-row     — icon + text, text gets word-break
        • Avatar sizes capped at 72px so two circles never exceed ~155px
          on a 320px phone, leaving the details column plenty of room.
        ══════════════════════════════════════════════════════════════
      */}
        <style>{`
        .st4-contact-inner {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: flex-start;
          gap: 1.5rem;
        }
        .st4-avatar-circle {
          width: 150px;
          height: 180px;
          border-radius: 18px;
          overflow: hidden;
        
          flex-shrink: 0;
          object-fit: cover;
        }
        .st4-company-logo {
          width: 180px;
          height: 180px;
          border-radius: 18px;
          overflow: hidden;
         
          flex-shrink: 0;
          object-fit: cover;
        }
        .st4-avatar-placeholder {
          width: 150px;
          height: 180px;
          border-radius: 18px;
       
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .st4-company-placeholder {
          width: 180px;
          height: 180px;
          border-radius: 18px;
       
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .st4-details {
          width: auto;
          text-align: center;
          overflow: hidden;
          flex: 1;
          min-width: 0;
        }
        .st4-agent-name {
          overflow-wrap: break-word;
          word-break: break-word;
          text-align: center;
        }
        .st4-contact-row {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          gap: 0.35rem;
        }
        .st4-contact-text {
          min-width: 0;
          flex: 0 1 auto;
          overflow-wrap: break-word;
          word-break: break-all;
          text-align: left;
        }
        @media (min-width: 768px) {
          .st4-avatar-circle,
          .st4-avatar-placeholder {
            width: 150px;
            height: 180px;
          }
          .st4-company-logo,
          .st4-company-placeholder {
            width: 180px;
            height: 180px;
          }
        }
      `}</style>

        {/* Saving Indicator */}
        {savingField && (
          <div className="absolute top-4 right-4 z-50 bg-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-lg  flex items-center gap-2 border border-amber-500/30">
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
            <span className="text-sm text-slate-300">Saving...</span>
          </div>
        )}

        {/* TWO LARGE PHOTOS */}
        <div className="p-5 md:p-8">
          <div className="grid grid-cols-[380px_380px] justify-center gap-5 md:gap-7">
            {/* Hero Image */}
            <div ref={heroContainerRef} className="relative rounded-2xl overflow-hidden bg-slate-800/60 border-2 border-slate-700 group lg:aspect-auto lg:h-96"
              style={{
                height: '380px',
                maxHeight: '380px',
                minHeight: '380px',
                width: '380px',
                minWidth: '380px',
                maxWidth: '380px',
              }}
            >
              {!readOnly && boxHeroRes && (
                <div className="absolute bottom-3 left-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxHeroRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {heroImageUrl ? (
                <>
                  <img src={heroImageUrl} alt="Main service image" className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" onClick={() => heroInputRef.current?.click()} className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30">
                        <Upload className="h-4 w-4 mr-2" />Change Main Photo
                      </Button>
                      <Button size="sm" onClick={openEditImage} className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30">
                        <Crop className="h-4 w-4 mr-2" />Edit
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <label className="h-full flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:text-amber-400 transition-colors"
                  onClick={() => !readOnly && heroInputRef.current?.click()}>
                  <ImageIcon className="w-16 h-16 mb-4" />
                  <p className="text-xl font-bold">Main Photo</p>
                  <p className="text-sm mt-2 opacity-80">{readOnly ? "No image" : "Click to upload"}</p>
                </label>
              )}
              <input ref={heroInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleHeroImageUpload(f); e.target.value = ""; }} />
              {!readOnly && (
                <ImageCropModal
                  open={cropModal.open}
                  imageUrl={cropModal.imageUrl}
                  title="Adjust Main Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleEditImageConfirm}
                  onCancel={handleEditImageCancel}
                />
              )}
            </div>

            {/* Secondary Image */}
            <div ref={secondaryContainerRef} className="relative rounded-2xl overflow-hidden bg-slate-800/60 border-2 border-slate-700 group"
              style={{
                height: '380px',
                maxHeight: '380px',
                minHeight: '380px',
                width: '380px',
                minWidth: '380px',
                maxWidth: '380px',
              }}
            >
              {!readOnly && boxSecondaryRes && (
                <div className="absolute bottom-3 left-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxSecondaryRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {secondaryImageUrl ? (
                <>
                  <img src={secondaryImageUrl} alt="Secondary view" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" onClick={() => secondaryInputRef.current?.click()} className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30">
                        <Upload className="h-4 w-4 mr-2" />Change Second Photo
                      </Button>
                      <Button size="sm" onClick={openEditSecondaryImage} className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30">
                        <Crop className="h-4 w-4 mr-2" />Edit
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <label className="h-full flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:text-amber-400 transition-colors"
                  onClick={() => !readOnly && secondaryInputRef.current?.click()}>
                  <ImageIcon className="w-16 h-16 mb-4" />
                  <p className="text-xl font-bold">Secondary Photo</p>
                </label>
              )}
              <input ref={secondaryInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSecondaryImageUpload(f); e.target.value = ""; }} />
              {!readOnly && (
                <ImageCropModal
                  open={secondaryCropModal.open}
                  imageUrl={secondaryCropModal.imageUrl}
                  title="Adjust Secondary Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleSecondaryEditImageConfirm}
                  onCancel={handleSecondaryEditImageCancel}
                />
              )}
            </div>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="px-6 md:px-10 py-10 md:py-12 space-y-10 md:space-y-12 bg-gradient-to-b from-slate-900 to-slate-950">

          {/* TITLE */}
          <div className="text-center w-full overflow-hidden px-2">
            <EditableText
              key={`title-${incomingSyncKey}`}
              value={serviceData.title ?? ""}
              defaultValue={title}
              onChange={(v) => { handleChange("title", v); saveFieldToBackend("title", v); }}
              readOnly={readOnly}
              as="h1"
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-amber-400 leading-tight break-words"
              inputClassName="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-center"
            />
          </div>

          {/* DESCRIPTION */}
          <div className="max-w-4xl mx-auto text-center w-full overflow-hidden px-2">
            <EditableText
              key={`desc-${incomingSyncKey}`}
              value={serviceData.description ?? ""}
              defaultValue={description}
              onChange={(v) => { handleChange("description", v); saveFieldToBackend("description", v); }}
              readOnly={readOnly}
              multiline
              as="p"
              className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-300 leading-relaxed break-words"
              inputClassName="text-sm sm:text-base md:text-lg lg:text-xl text-center"
            />
          </div>

          {/* FEATURES SECTION */}
          <div>
            <h3 className="text-amber-400 font-bold text-xl md:text-2xl mb-6 flex items-center justify-between gap-3 flex-wrap">
              <span className="flex items-center break-words">Our Key Services</span>
              {!readOnly && (
                <span className={`text-sm font-normal whitespace-nowrap ${features.length >= MAX_FEATURES
                  ? "text-amber-600"
                  : "text-slate-400"
                  }`}>
                  {features.length}/{MAX_FEATURES}
                </span>
              )}
            </h3>

            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {features.map((feat, i) => (
                <div key={feat.id}
                  className="flex items-center min-h-[72px] w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 hover:bg-slate-800/70 hover:border-slate-600 transition-all group overflow-hidden">
                  <div className="w-4 h-4 bg-amber-400 rounded-full mr-4 flex-shrink-0" />
                  {readOnly ? (
                    <span className="flex-1 text-white text-base md:text-lg min-w-0 break-words" title={feat.feature_name}>
                      {feat.feature_name}
                    </span>
                  ) : (
                    <input type="text" value={feat.feature_name}
                      onChange={(e) => handleFeatureChange(i, e.target.value)}
                      onBlur={() => handleFeatureBlur(i)}
                      className="flex-1 min-w-0 bg-transparent border-none outline-none text-white text-base md:text-lg placeholder-slate-500 truncate"
                      maxLength={30} placeholder="e.g., Financial Planning" />
                  )}
                  {!readOnly && features.length > 1 && (
                    <button onClick={() => handleFeatureDelete(i)}
                      className="ml-3 text-red-400 hover:text-red-300 text-2xl opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">×</button>
                  )}
                </div>
              ))}

              {!readOnly && features.length < MAX_FEATURES && (
                <button onClick={addFeature}
                  className="col-span-1 md:col-span-2 flex items-center justify-center gap-2 h-14 border-2 border-dashed border-slate-600 rounded-xl text-slate-400 hover:border-amber-500 hover:text-amber-400 transition-all">
                  <Plus className="w-5 h-5" />
                  <span>Add Service</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════
  CONTACT CARD — FULLY RESPONSIVE
  ════════════════════════════════════════════════════════════ */}
        <div className="px-4 sm:px-5 md:px-8 py-10 border-t border-slate-700 bg-gradient-to-t from-slate-950 to-slate-900">
          <div className="bg-slate-800/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-3 sm:p-3 md:p-3">

            <div className="st4-contact-inner">

              {/* Profile photo */}
              <div className="relative flex-shrink-0">
                {displayAvatar ? (
                  <img src={displayAvatar} alt="Profile" className="st4-avatar-circle" />
                ) : (
                  <div className="st4-avatar-placeholder">
                    <User className="w-8 h-8 text-slate-500" />
                  </div>
                )}
              </div>

              {/* ── Details ── */}
              <div className="st4-details">

                {/* Agent name */}
                <p
                  className="st4-agent-name text-xl sm:text-2xl md:text-3xl font-bold text-amber-200 mb-4 sm:mb-5 leading-snug"
                  title={agentFullName}
                  style={{ color: '#fbbe25' }}
                >
                  {agentFullName}
                </p>

                {/* Contact rows */}
                <div className="space-y-3 text-slate-300 text-sm sm:text-base flex flex-col items-center">

                  {/* Phone */}
                  {displayPhone && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="font-medium">{displayPhone}</span>
                    </div>
                  )}

                  {/* Email */}
                  {displayEmail && (
                    <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                      <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="font-medium underline">
                        {/* {displayEmail} */}
                          Click here to email Me
                        </span>
                    </div>
                  )}

                  {/* Company */}
                  {displayCompanyName && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="font-medium">{displayCompanyName}</span>
                    </div>
                  )}

                  {/* License Number */}
                  {userData?.license_number && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <IdCard className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="font-medium">License #: {userData.license_number}</span>
                    </div>
                  )}

                  {/* Address */}
                  {displayAddress && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="font-medium">{displayAddress}</span>
                    </div>
                  )}

                </div>
              </div>

              {/* Company logo */}
              {displayCompanyLogo ? (
                <img src={displayCompanyLogo} alt="Company Logo" className="st4-company-logo" />
              ) : (
                <div className="st4-company-placeholder">
                  <Building2 className="w-8 h-8 text-slate-500" />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
